import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Mail, User, Phone, Lock, ShieldCheck, MapPin } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADOS_BRASILEIROS = [
  { uf: 'AC', nome: 'Acre' }, { uf: 'AL', nome: 'Alagoas' }, { uf: 'AP', nome: 'Amapá' },
  { uf: 'AM', nome: 'Amazonas' }, { uf: 'BA', nome: 'Bahia' }, { uf: 'CE', nome: 'Ceará' },
  { uf: 'DF', nome: 'Distrito Federal' }, { uf: 'ES', nome: 'Espírito Santo' },
  { uf: 'GO', nome: 'Goiás' }, { uf: 'MA', nome: 'Maranhão' }, { uf: 'MT', nome: 'Mato Grosso' },
  { uf: 'MS', nome: 'Mato Grosso do Sul' }, { uf: 'MG', nome: 'Minas Gerais' },
  { uf: 'PA', nome: 'Pará' }, { uf: 'PB', nome: 'Paraíba' }, { uf: 'PR', nome: 'Paraná' },
  { uf: 'PE', nome: 'Pernambuco' }, { uf: 'PI', nome: 'Piauí' }, { uf: 'RJ', nome: 'Rio de Janeiro' },
  { uf: 'RN', nome: 'Rio Grande do Norte' }, { uf: 'RS', nome: 'Rio Grande do Sul' },
  { uf: 'RO', nome: 'Rondônia' }, { uf: 'RR', nome: 'Roraima' }, { uf: 'SC', nome: 'Santa Catarina' },
  { uf: 'SP', nome: 'São Paulo' }, { uf: 'SE', nome: 'Sergipe' }, { uf: 'TO', nome: 'Tocantins' }
];

export default function Register() {
  usePageTitle('Cadastro');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: email, 2: complete form
  const [email, setEmail] = useState('');
  const [municipios, setMunicipios] = useState([]);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    uf: '',
    municipio: '',
    password: '',
    verification_code: ''
  });

  useEffect(() => {
    if (formData.uf) {
      fetchMunicipios(formData.uf);
    }
  }, [formData.uf]);

  const fetchMunicipios = async (uf) => {
    setLoadingMunicipios(true);
    try {
      const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`);
      if (response.ok) {
        const data = await response.json();
        setMunicipios(data.map(m => m.nome).sort());
      }
    } catch (error) {
      console.error('Erro ao carregar municípios:', error);
      toast.error('Erro ao carregar municípios');
    } finally {
      setLoadingMunicipios(false);
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Código de verificação enviado para seu email!');
        setStep(2);
      } else {
        toast.error(data.detail || 'Erro ao enviar código');
      }
    } catch (error) {
      toast.error('Erro de conexão com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/resend-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Novo código enviado!');
      } else {
        toast.error(data.detail || 'Erro ao reenviar código');
      }
    } catch (error) {
      toast.error('Erro de conexão com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          ...formData
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Cadastro realizado com sucesso!');
        localStorage.setItem('coagenda_token', data.access_token);
        navigate('/dashboard');
      } else {
        toast.error(data.detail || 'Erro ao cadastrar');
      }
    } catch (error) {
      toast.error('Erro de conexão com o servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container" data-testid="register-page">
      <div className="auth-card fade-in">
        <div className="auth-logo">
          <img src="/coagenda-logo.png" alt="CoAgenda" style={{ width: '180px', height: 'auto', margin: '0 auto 1rem' }} />
          <p>{step === 1 ? 'Crie sua conta' : 'Complete seu cadastro'}</p>
        </div>

        {step === 1 ? (
          <form onSubmit={handleSendCode} data-testid="email-form">
            <div style={{ marginBottom: '1.5rem' }}>
              <Label htmlFor="email">Email</Label>
              <div style={{ position: 'relative', marginTop: '0.5rem' }}>
                <Mail size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                  style={{ paddingLeft: '2.5rem' }}
                />
              </div>
              <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.5rem' }}>
                Enviaremos um código de verificação para este email
              </p>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="send-code-button"
              style={{ marginTop: '1rem' }}
            >
              {loading ? 'Enviando...' : 'Enviar código de verificação'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleRegister} data-testid="register-form">
            <div style={{ marginBottom: '1.25rem' }}>
              <Label htmlFor="code">Código de Verificação</Label>
              <div style={{ position: 'relative', marginTop: '0.5rem' }}>
                <ShieldCheck size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <Input
                  id="code"
                  type="text"
                  placeholder="000000"
                  value={formData.verification_code}
                  onChange={(e) => setFormData({ ...formData, verification_code: e.target.value })}
                  required
                  maxLength={6}
                  data-testid="code-input"
                  style={{ paddingLeft: '2.5rem', textAlign: 'center', letterSpacing: '0.3rem' }}
                />
              </div>
              <div style={{ marginTop: '0.5rem', textAlign: 'right' }}>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleResendCode}
                  disabled={loading}
                  data-testid="resend-code-button"
                  style={{ color: '#0ea5e9', fontSize: '0.85rem' }}
                >
                  Reenviar código
                </Button>
              </div>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <Label htmlFor="nome">Nome Completo</Label>
              <div style={{ position: 'relative', marginTop: '0.5rem' }}>
                <User size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <Input
                  id="nome"
                  type="text"
                  placeholder="Seu nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                  data-testid="nome-input"
                  style={{ paddingLeft: '2.5rem' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <Label htmlFor="telefone">Telefone (opcional)</Label>
              <div style={{ position: 'relative', marginTop: '0.5rem' }}>
                <Phone size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <Input
                  id="telefone"
                  type="tel"
                  placeholder="(00) 00000-0000"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  data-testid="telefone-input"
                  style={{ paddingLeft: '2.5rem' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
              <div>
                <Label htmlFor="uf">Estado (opcional)</Label>
                <div style={{ position: 'relative', marginTop: '0.5rem' }}>
                  <MapPin size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', zIndex: 10 }} />
                  <Select value={formData.uf} onValueChange={(value) => setFormData({ ...formData, uf: value, municipio: '' })}>
                    <SelectTrigger data-testid="uf-select" style={{ paddingLeft: '2.5rem' }}>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {ESTADOS_BRASILEIROS.map((estado) => (
                        <SelectItem key={estado.uf} value={estado.uf}>
                          {estado.uf} - {estado.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="municipio">Município (opcional)</Label>
                <div style={{ position: 'relative', marginTop: '0.5rem' }}>
                  <MapPin size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', zIndex: 10 }} />
                  <Select 
                    value={formData.municipio} 
                    onValueChange={(value) => setFormData({ ...formData, municipio: value })}
                    disabled={!formData.uf || loadingMunicipios}
                  >
                    <SelectTrigger data-testid="municipio-select" style={{ paddingLeft: '2.5rem' }}>
                      <SelectValue placeholder={loadingMunicipios ? 'Carregando...' : 'Selecione'} />
                    </SelectTrigger>
                    <SelectContent>
                      {municipios.map((municipio) => (
                        <SelectItem key={municipio} value={municipio}>
                          {municipio}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <Label htmlFor="password">Senha</Label>
              <div style={{ position: 'relative', marginTop: '0.5rem' }}>
                <Lock size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="password-input"
                  style={{ paddingLeft: '2.5rem' }}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="register-button"
              style={{ marginTop: '1rem' }}
            >
              {loading ? 'Cadastrando...' : 'Criar conta'}
            </Button>

            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setStep(1)}
                data-testid="back-button"
                style={{ color: '#64748b' }}
              >
                ← Voltar para email
              </Button>
            </div>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: '1.5rem', color: '#64748b', fontSize: '0.9rem' }}>
          Já tem uma conta?{' '}
          <Link to="/login" style={{ color: '#0ea5e9', fontWeight: 600, textDecoration: 'none' }} data-testid="login-link">
            Faça login
          </Link>
        </div>
      </div>
    </div>
  );
}
